<script>
  import { onMount } from 'svelte';
  import AgGridSvelte from 'ag-grid-svelte';
  import 'ag-grid-community/styles/ag-grid.css';
  import 'ag-grid-community/styles/ag-theme-alpine.css';
  import { API_BASE_URL, isAdminUser } from './config.js';
  import FormModal from './components/modals/FormModal.svelte';
  import ConfirmModal from './components/modals/ConfirmModal.svelte';
  import Button from './components/common/Button.svelte';
  import Navigation from './components/layout/Navigation.svelte';
  import './styles/main.css';

  export let setPage;
  export let user;
  export let onLogout = null;

  let isAdmin = false;
  let isCompany = false;

  if (typeof window !== 'undefined') {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        user = JSON.parse(storedUser);
        isAdmin = isAdminUser(user);
        isCompany = user.role === 'company';
      } catch (e) {
        console.error('사용자 정보 파싱 오류:', e);
      }
    }
  }

  // 데이터 상태
  let rowData = [];
  let gridApi;
  let selectedInterview = null;
  let isLoading = true;
  let loadError = null;

  // 모달 상태
  let isCreateModalOpen = false;
  let isEditModalOpen = false;
  let isDeleteModalOpen = false;
  let isStatusModalOpen = false;

  // 필터 상태
  let quickFilterText = '';
  let statusFilter = '';

  // 상태 정의
  const statusList = [
    { value: 'applied', label: '지원', color: '#3b82f6', bgColor: '#dbeafe' },
    { value: 'scheduled', label: '예정', color: '#f59e0b', bgColor: '#fef3c7' },
    { value: 'completed', label: '완료', color: '#10b981', bgColor: '#d1fae5' },
    { value: 'rejected', label: '불합격', color: '#ef4444', bgColor: '#fee2e2' },
    { value: 'accepted', label: '합격', color: '#059669', bgColor: '#a7f3d0' }
  ];

  function getStatusInfo(status) {
    return statusList.find(s => s.value === status) || { value: status, label: status, color: '#6b7280', bgColor: '#f3f4f6' };
  }

  // 폼 데이터
  let createForm = {
    projectName: '',
    candidateName: '',
    interviewDate: '',
    location: '',
    contactChannel: '',
    notes: '',
    status: 'applied'
  };

  let editForm = {
    id: null,
    projectName: '',
    candidateName: '',
    interviewDate: '',
    location: '',
    contactChannel: '',
    notes: '',
    status: 'applied'
  };

  let statusChangeForm = {
    id: null,
    status: ''
  };

  // 날짜 포맷 함수
  function formatDateForInput(dateValue) {
    if (!dateValue) return '';
    if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(dateValue)) {
      return dateValue.slice(0, 16); // datetime-local용
    }
    if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
      return dateValue;
    }
    const d = new Date(dateValue);
    if (isNaN(d)) return '';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const h = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${y}-${m}-${day}T${h}:${min}`;
  }

  function formatDateDisplay(dateValue) {
    if (!dateValue) return '';
    const d = new Date(dateValue);
    if (isNaN(d)) return '';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const h = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${y}-${m}-${day} ${h}:${min}`;
  }

  // 상태 셀 렌더러
  function statusCellRenderer(params) {
    const info = getStatusInfo(params.value);
    return `<span style="
      display: inline-block;
      padding: 4px 12px;
      border-radius: 12px;
      font-size: 12px;
      font-weight: 600;
      color: ${info.color};
      background-color: ${info.bgColor};
    ">${info.label}</span>`;
  }

  // AG Grid 컬럼 정의
  const columnDefs = [
    {
      headerName: '',
      width: 50,
      checkboxSelection: true,
      headerCheckboxSelection: false,
      pinned: 'left',
      sortable: false,
      filter: false,
      resizable: false,
      suppressMenu: true,
      lockPosition: true
    },
    {
      headerName: '진행상황',
      field: 'status',
      sortable: true,
      filter: true,
      width: 110,
      cellRenderer: statusCellRenderer
    },
    {
      headerName: '프로젝트명',
      field: 'project_name',
      sortable: true,
      filter: true,
      flex: 1,
      minWidth: 200
    },
    {
      headerName: '대상자',
      field: 'candidate_name',
      sortable: true,
      filter: true,
      width: 120
    },
    {
      headerName: '면접일시',
      field: 'interview_date',
      sortable: true,
      filter: true,
      width: 160,
      sort: 'asc',
      sortIndex: 0,
      valueFormatter: params => formatDateDisplay(params.value),
      comparator: (valueA, valueB) => {
        // null(미정)은 항상 맨 아래로
        if (!valueA && !valueB) return 0;
        if (!valueA) return 1;
        if (!valueB) return -1;
        return new Date(valueA).getTime() - new Date(valueB).getTime();
      }
    },
    {
      headerName: '장소',
      field: 'location',
      sortable: true,
      filter: true,
      width: 200
    },
    {
      headerName: '채널(담당자)',
      field: 'contact_channel',
      sortable: true,
      filter: true,
      width: 180
    },
    {
      headerName: '참고사항',
      field: 'notes',
      sortable: true,
      filter: true,
      width: 200,
      tooltipField: 'notes'
    },
    {
      headerName: '등록일',
      field: 'created_at',
      sortable: true,
      filter: true,
      width: 120,
      valueFormatter: params => {
        if (!params.value) return '';
        const d = new Date(params.value);
        if (isNaN(d)) return '';
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
      }
    }
  ];

  const gridOptions = {
    domLayout: 'normal',
    rowSelection: 'single',
    defaultColDef: {
      resizable: true,
      unSortIcon: true  // 헤더 클릭 시 기본 → 오름차순 → 내림차순 3단계 순환
    },
    suppressRowClickSelection: true,
    rowHeight: 48,
    tooltipShowDelay: 500
  };

  function onGridReady(params) {
    gridApi = params.api;
  }

  function onSelectionChanged() {
    const selectedRows = gridApi ? gridApi.getSelectedRows() : [];
    selectedInterview = selectedRows[0] || null;
  }

  function onCellClicked(params) {
    if (params.column.colId === '0') return; // 체크박스 컬럼 무시
    selectedInterview = params.data;
    openEditModal();
  }

  // ===================================
  // API 호출 함수들
  // ===================================

  async function fetchInterviews() {
    isLoading = true;
    loadError = null;
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({ page: '1', limit: '1000' });
      if (statusFilter) params.set('status', statusFilter);

      const response = await fetch(`${API_BASE_URL}/api/interviews?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('인터뷰 목록을 불러오는 데 실패했습니다.');
      }

      const result = await response.json();
      rowData = result.data || [];
    } catch (error) {
      console.error('인터뷰 목록 조회 오류:', error);
      loadError = error.message;
    } finally {
      isLoading = false;
    }
  }

  async function createInterview() {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/interviews`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(createForm)
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.message || '등록 실패');
      }

      isCreateModalOpen = false;
      await fetchInterviews();
    } catch (error) {
      alert(error.message);
    }
  }

  async function updateInterview() {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/interviews/${editForm.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(editForm)
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.message || '수정 실패');
      }

      isEditModalOpen = false;
      selectedInterview = null;
      await fetchInterviews();
    } catch (error) {
      alert(error.message);
    }
  }

  async function deleteInterview() {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/interviews/${selectedInterview.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.message || '삭제 실패');
      }

      isDeleteModalOpen = false;
      selectedInterview = null;
      await fetchInterviews();
    } catch (error) {
      alert(error.message);
    }
  }

  async function changeStatus() {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/interviews/${statusChangeForm.id}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: statusChangeForm.status })
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.message || '상태 변경 실패');
      }

      isStatusModalOpen = false;
      selectedInterview = null;
      await fetchInterviews();
    } catch (error) {
      alert(error.message);
    }
  }

  // ===================================
  // 모달 열기/닫기
  // ===================================

  function openCreateModal() {
    createForm = {
      projectName: '',
      candidateName: '',
      interviewDate: '',
      location: '',
      contactChannel: '',
      notes: '',
      status: 'applied'
    };
    isCreateModalOpen = true;
  }

  function openEditModal() {
    if (!selectedInterview) return;
    editForm = {
      id: selectedInterview.id,
      projectName: selectedInterview.project_name || '',
      candidateName: selectedInterview.candidate_name || '',
      interviewDate: formatDateForInput(selectedInterview.interview_date),
      location: selectedInterview.location || '',
      contactChannel: selectedInterview.contact_channel || '',
      notes: selectedInterview.notes || '',
      status: selectedInterview.status || 'applied'
    };
    isEditModalOpen = true;
  }

  function openDeleteModal() {
    if (!selectedInterview) return;
    isDeleteModalOpen = true;
  }

  function openStatusModal() {
    if (!selectedInterview) return;
    statusChangeForm = {
      id: selectedInterview.id,
      status: selectedInterview.status
    };
    isStatusModalOpen = true;
  }

  // 상태 필터 변경
  async function handleStatusFilter(status) {
    statusFilter = statusFilter === status ? '' : status;
    await fetchInterviews();
  }

  // 빠른 필터
  $: if (gridApi) {
    gridApi.setQuickFilter(quickFilterText);
  }

  onMount(() => {
    fetchInterviews();
  });
</script>

<main class="admin-page">
  <Navigation {user} currentPage="admin-interviews" {onLogout} />

  <div class="admin-content">
    <div class="admin-header">
      <h2>인터뷰 관리</h2>
      <p class="admin-subtitle">지원 및 면접 정보를 관리합니다.</p>
    </div>

    <!-- 상태 필터 탭 -->
    <div class="status-filter-tabs">
      <button
        class="filter-tab"
        class:active={statusFilter === ''}
        on:click={() => handleStatusFilter('')}
      >
        전체
      </button>
      {#each statusList as st}
        <button
          class="filter-tab"
          class:active={statusFilter === st.value}
          on:click={() => handleStatusFilter(st.value)}
          style="--tab-color: {st.color}; --tab-bg: {st.bgColor};"
        >
          {st.label}
        </button>
      {/each}
    </div>

    <!-- 도구 모음 -->
    <div class="toolbar">
      <div class="toolbar-left">
        <input
          type="text"
          class="search-input"
          placeholder="프로젝트명, 대상자 검색..."
          bind:value={quickFilterText}
        />
      </div>
      <div class="toolbar-right">
        <Button variant="primary" on:click={openCreateModal}>등록</Button>
        <Button variant="default" on:click={openEditModal} disabled={!selectedInterview}>수정</Button>
        <Button variant="default" on:click={openStatusModal} disabled={!selectedInterview}>진행상황</Button>
        <Button variant="danger" on:click={openDeleteModal} disabled={!selectedInterview}>삭제</Button>
      </div>
    </div>

    <!-- 데이터 그리드 -->
    {#if isLoading}
      <div class="loading-container">
        <div class="loader"></div>
        <p>데이터를 불러오는 중입니다...</p>
      </div>
    {:else if loadError}
      <div class="error-container">
        <div class="error-icon">!</div>
        <h3>데이터를 불러오지 못했습니다</h3>
        <p>{loadError}</p>
        <button class="retry-btn" on:click={fetchInterviews}>다시 시도</button>
      </div>
    {:else}
      <div class="admin-table-wrapper">
        <div class="ag-theme-alpine custom-grid" style="width: 100%; height: 600px;">
          <AgGridSvelte
            {rowData}
            {columnDefs}
            {gridOptions}
            pagination={true}
            paginationPageSize={20}
            rowSelection="single"
            getRowId={(params) => String(params.data.id)}
            onGridReady={onGridReady}
            onSelectionChanged={onSelectionChanged}
            onCellClicked={onCellClicked}
          />
        </div>
      </div>
    {/if}
  </div>
</main>

<!-- 인터뷰 등록 모달 -->
<FormModal
  bind:isOpen={isCreateModalOpen}
  title="인터뷰 등록"
  submitText="등록"
  onSubmit={createInterview}
  onCancel={() => (isCreateModalOpen = false)}
>
  <div class="form-group">
    <label for="create-status">진행상황</label>
    <select id="create-status" bind:value={createForm.status}>
      {#each statusList as st}
        <option value={st.value}>{st.label}</option>
      {/each}
    </select>
  </div>
  <div class="form-group">
    <label for="create-projectName">프로젝트명 <span class="required">*</span></label>
    <input id="create-projectName" type="text" bind:value={createForm.projectName} placeholder="예: 인사시스템 운영 유지보수(중급/JAVA/6개월/구반포역)" required />
  </div>
  <div class="form-group">
    <label for="create-candidateName">대상자 <span class="required">*</span></label>
    <input id="create-candidateName" type="text" bind:value={createForm.candidateName} placeholder="예: 김정민" required />
  </div>
  <div class="form-group">
    <label for="create-interviewDate">면접 일시</label>
    <input id="create-interviewDate" type="datetime-local" bind:value={createForm.interviewDate} />
  </div>
  <div class="form-group">
    <label for="create-location">장소</label>
    <input id="create-location" type="text" bind:value={createForm.location} placeholder="예: 서울시 서초구 사평대로 60 덕명빌딩 4F" />
  </div>
  <div class="form-group">
    <label for="create-contactChannel">채널 (담당자/연락처)</label>
    <input id="create-contactChannel" type="text" bind:value={createForm.contactChannel} placeholder="예: 유충현 수석(010-6847-3780)" />
  </div>
  <div class="form-group">
    <label for="create-notes">참고사항</label>
    <textarea id="create-notes" bind:value={createForm.notes} rows="4" placeholder="예: 투입시 장비지참, 윈도11프로 설치"></textarea>
  </div>
</FormModal>

<!-- 인터뷰 수정 모달 -->
<FormModal
  bind:isOpen={isEditModalOpen}
  title="인터뷰 수정"
  submitText="수정"
  onSubmit={updateInterview}
  onCancel={() => (isEditModalOpen = false)}
>
  <div class="form-group">
    <label for="edit-status">진행상황</label>
    <select id="edit-status" bind:value={editForm.status}>
      {#each statusList as st}
        <option value={st.value}>{st.label}</option>
      {/each}
    </select>
  </div>
  <div class="form-group">
    <label for="edit-projectName">프로젝트명 <span class="required">*</span></label>
    <input id="edit-projectName" type="text" bind:value={editForm.projectName} required />
  </div>
  <div class="form-group">
    <label for="edit-candidateName">대상자 <span class="required">*</span></label>
    <input id="edit-candidateName" type="text" bind:value={editForm.candidateName} required />
  </div>
  <div class="form-group">
    <label for="edit-interviewDate">면접 일시</label>
    <input id="edit-interviewDate" type="datetime-local" bind:value={editForm.interviewDate} />
  </div>
  <div class="form-group">
    <label for="edit-location">장소</label>
    <input id="edit-location" type="text" bind:value={editForm.location} />
  </div>
  <div class="form-group">
    <label for="edit-contactChannel">채널 (담당자/연락처)</label>
    <input id="edit-contactChannel" type="text" bind:value={editForm.contactChannel} />
  </div>
  <div class="form-group">
    <label for="edit-notes">참고사항</label>
    <textarea id="edit-notes" bind:value={editForm.notes} rows="4"></textarea>
  </div>
</FormModal>

<!-- 삭제 확인 모달 -->
<ConfirmModal
  bind:isOpen={isDeleteModalOpen}
  title="인터뷰 삭제"
  message={selectedInterview ? `"${selectedInterview.project_name}" - ${selectedInterview.candidate_name} 인터뷰를 삭제하시겠습니까?` : ''}
  confirmText="삭제"
  onConfirm={deleteInterview}
  onCancel={() => (isDeleteModalOpen = false)}
/>

<!-- 상태 변경 모달 -->
<FormModal
  bind:isOpen={isStatusModalOpen}
  title="진행상황 변경"
  submitText="변경"
  onSubmit={changeStatus}
  onCancel={() => (isStatusModalOpen = false)}
>
  <div class="status-change-container">
    <p class="status-change-info">
      {#if selectedInterview}
        <strong>{selectedInterview.candidate_name}</strong>님의 진행상황을 변경합니다.
      {/if}
    </p>
    <div class="status-buttons">
      {#each statusList as st}
        <button
          class="status-select-btn"
          class:selected={statusChangeForm.status === st.value}
          style="--btn-color: {st.color}; --btn-bg: {st.bgColor};"
          on:click={() => (statusChangeForm.status = st.value)}
          type="button"
        >
          {st.label}
        </button>
      {/each}
    </div>
  </div>
</FormModal>

<style>
  .admin-page {
    min-height: 100vh;
    background-color: #f8f9fa;
  }

  .admin-content {
    max-width: 1400px;
    margin: 0 auto;
    padding: 24px;
    position: relative;
    z-index: 1;
  }

  .admin-header {
    margin-bottom: 24px;
  }

  .admin-header h2 {
    font-size: 1.5rem;
    font-weight: 700;
    color: #1a1a1a;
    margin: 0 0 4px 0;
  }

  .admin-subtitle {
    color: #6b7280;
    font-size: 0.9rem;
    margin: 0;
  }

  /* 상태 필터 탭 */
  .status-filter-tabs {
    display: flex;
    gap: 8px;
    margin-bottom: 16px;
    flex-wrap: wrap;
  }

  .filter-tab {
    padding: 6px 16px;
    border: 1px solid #d1d5db;
    border-radius: 20px;
    background: white;
    color: #4b5563;
    font-size: 0.85rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
  }

  .filter-tab:hover {
    border-color: var(--tab-color, #3b82f6);
    color: var(--tab-color, #3b82f6);
  }

  .filter-tab.active {
    background-color: var(--tab-bg, #dbeafe);
    border-color: var(--tab-color, #3b82f6);
    color: var(--tab-color, #3b82f6);
  }

  /* 도구 모음 */
  .toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;
    flex-wrap: wrap;
  }

  .toolbar-left {
    display: flex;
    gap: 8px;
    flex: 1;
    min-width: 200px;
  }

  .toolbar-right {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }

  .search-input {
    padding: 8px 12px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 0.9rem;
    width: 100%;
    max-width: 350px;
    outline: none;
    transition: border-color 0.2s;
  }

  .search-input:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }

  /* 그리드 래퍼 */
  .admin-table-wrapper {
    background: white;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  :global(.custom-grid .ag-header-cell-label) {
    font-weight: 600;
  }

  :global(.custom-grid .ag-row) {
    cursor: pointer;
  }

  :global(.custom-grid .ag-row:hover) {
    background-color: #f0f7ff !important;
  }

  /* 로딩 & 에러 */
  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    color: #6b7280;
  }

  .loader {
    width: 40px;
    height: 40px;
    border: 3px solid #e5e7eb;
    border-top: 3px solid #3b82f6;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 16px;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .error-container {
    text-align: center;
    padding: 60px 20px;
    color: #6b7280;
  }

  .error-icon {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: #fee2e2;
    color: #ef4444;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    font-weight: bold;
    margin-bottom: 12px;
  }

  .retry-btn {
    margin-top: 12px;
    padding: 8px 20px;
    background: #3b82f6;
    color: white;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    font-size: 0.9rem;
  }

  .retry-btn:hover {
    background: #2563eb;
  }

  /* 폼 스타일 */
  .form-group {
    margin-bottom: 16px;
  }

  .form-group label {
    display: block;
    margin-bottom: 6px;
    font-weight: 600;
    font-size: 0.85rem;
    color: #374151;
  }

  .form-group input,
  .form-group select,
  .form-group textarea {
    width: 100%;
    padding: 10px 12px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 0.9rem;
    outline: none;
    transition: border-color 0.2s;
    box-sizing: border-box;
  }

  .form-group input:focus,
  .form-group select:focus,
  .form-group textarea:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }

  .form-group textarea {
    resize: vertical;
  }

  .required {
    color: #ef4444;
  }

  /* 상태 변경 모달 */
  .status-change-container {
    text-align: center;
  }

  .status-change-info {
    margin-bottom: 20px;
    font-size: 0.95rem;
    color: #374151;
  }

  .status-buttons {
    display: flex;
    gap: 10px;
    justify-content: center;
    flex-wrap: wrap;
  }

  .status-select-btn {
    padding: 10px 20px;
    border: 2px solid #d1d5db;
    border-radius: 8px;
    background: white;
    color: #4b5563;
    font-size: 0.95rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
  }

  .status-select-btn:hover {
    border-color: var(--btn-color);
    color: var(--btn-color);
    background-color: var(--btn-bg);
  }

  .status-select-btn.selected {
    border-color: var(--btn-color);
    color: var(--btn-color);
    background-color: var(--btn-bg);
    box-shadow: 0 0 0 2px var(--btn-color);
  }

  /* 반응형 */
  @media (max-width: 768px) {
    .admin-content {
      padding: 16px;
    }

    .toolbar {
      flex-direction: column;
    }

    .toolbar-left {
      width: 100%;
    }

    .toolbar-right {
      width: 100%;
      justify-content: flex-end;
    }

    .search-input {
      max-width: 100%;
    }

    .status-filter-tabs {
      gap: 6px;
    }

    .filter-tab {
      padding: 5px 12px;
      font-size: 0.8rem;
    }
  }
</style>
